<div class="environment-control-panel">

  <div class="env-header">
    <h4 class="env-title">
      Umgebung
      <span class="label label-purple stage-badge" *ngIf="stage">{{ stage }}</span>
    </h4>
    <button type="button" class="btn btn-sm btn-link" (click)="refresh()" [disabled]="loading || running">
      <cds-icon shape="refresh"></cds-icon> Aktualisieren
    </button>
    <clr-spinner clrSmall clrInline *ngIf="loading && status"></clr-spinner>
  </div>

  <p class="env-scope">
    <cds-icon shape="info-circle"></cds-icon>
    Steuert ausschließlich diese Umgebung. dev, stg und prd liegen in getrennten AWS-Konten —
    für eine andere Umgebung das dortige Portal öffnen.
  </p>

  <p class="env-last" *ngIf="status?.lastAction as last">
    Zuletzt: <strong>{{ last.action }}</strong> durch {{ last.actor }}
    am {{ last.atUtc | date:'dd.MM.yyyy HH:mm' }}
  </p>

  <!-- Aktionen -->
  <div class="env-actions" *hasPermission="'control-infrastructure'">
    <button type="button" class="btn btn-success-outline"
            (click)="activate()" [disabled]="!!running || loading">
      <clr-spinner clrSmall clrInline *ngIf="running === 'activate'"></clr-spinner>
      Aktivieren
    </button>
    <button type="button" class="btn btn-warning-outline"
            (click)="hibernate()" [disabled]="!!running || loading">
      <clr-spinner clrSmall clrInline *ngIf="running === 'hibernate'"></clr-spinner>
      Ruhezustand
    </button>
    <button type="button" class="btn btn-danger-outline"
            (click)="openDeactivateDialog()" [disabled]="!!running || loading">
      <clr-spinner clrSmall clrInline *ngIf="running === 'deactivate'"></clr-spinner>
      Deaktivieren
    </button>
  </div>

  <ul class="env-legend">
    <li><strong>Aktivieren</strong> — dauerhafte Dienste starten, ASG zurück auf die konfigurierte Kapazität. Geparkte GPU-Instanzen bleiben geparkt.</li>
    <li><strong>Ruhezustand</strong> — ASG angehalten und auf 0, dauerhafte Dienste gestoppt. Vollständig umkehrbar.</li>
    <li><strong>Deaktivieren</strong> — zusätzlich die wieder aufbaubaren GPU-Instanzen terminieren. Dauerhafte Dienste werden auch dabei nur gestoppt.</li>
  </ul>

  <!-- 409: es läuft noch etwas -->
  <div class="alert alert-warning" role="alert" *ngIf="busyWarning">
    <div class="alert-items">
      <div class="alert-item static">
        <cds-icon shape="exclamation-triangle" class="alert-icon"></cds-icon>
        <span class="alert-text">{{ busyWarning }}</span>
        <div class="alert-actions">
          <button class="btn alert-action" type="button" (click)="forceRetry()">
            Trotzdem ausführen — laufende Sitzungen enden sofort
          </button>
          <button class="btn alert-action" type="button" (click)="dismissBusyWarning()">Abbrechen</button>
        </div>
      </div>
    </div>
  </div>

  <div class="alert alert-danger" role="alert" *ngIf="error">
    <div class="alert-items">
      <div class="alert-item static">
        <cds-icon shape="exclamation-circle" class="alert-icon"></cds-icon>
        <span class="alert-text">{{ error }}</span>
      </div>
    </div>
  </div>

  <!-- Ergebnis der letzten Aktion -->
  <div class="alert alert-success" role="alert" *ngIf="lastResult as r">
    <div class="alert-items">
      <div class="alert-item static">
        <cds-icon shape="check-circle" class="alert-icon"></cds-icon>
        <span class="alert-text">
          {{ r.action }}: {{ r.started.length }} gestartet, {{ r.stopped.length }} gestoppt,
          {{ r.terminated.length }} terminiert, {{ r.autoScalingGroupsChanged.length }} ASG geändert.
        </span>
      </div>
      <div class="alert-item static" *ngFor="let note of r.notes">
        <cds-icon shape="info-circle" class="alert-icon"></cds-icon>
        <span class="alert-text">{{ note }}</span>
      </div>
    </div>
  </div>

  <div class="loading-row" *ngIf="loading && !status">
    <clr-spinner clrSmall clrInline></clr-spinner> Umgebung wird geladen…
  </div>

  <!-- Convergence toward the recorded target -->
  <div class="fleet-progress" *ngIf="status?.progress as p">
    <h5 class="section-title">
      Ziel: {{ targetLabel(p.target) }}
      <span class="label" [ngClass]="p.converged ? 'label-success' : 'label-warning'">
        {{ p.converged ? 'erreicht' : 'läuft…' }}
      </span>
    </h5>
    <p class="section-hint" *ngIf="!p.converged">
      Stoppen dauert: die transcribe-vod-ASG entleert über einen 1-Stunden-Lifecycle-Hook.
      Der Reconciler treibt den Zustand weiter, bis das Ziel erreicht ist.
    </p>

    <table class="table table-compact progress-table">
      <tbody>
        <tr *ngFor="let c of p.categories">
          <td class="left progress-name">{{ c.name }}</td>
          <td class="progress-count">
            <span class="label" [ngClass]="c.converged ? 'label-success' : 'label-warning'">
              {{ c.done }}/{{ c.total }}
            </span>
          </td>
          <td class="left progress-pending">
            <span class="muted" *ngIf="c.pending.length === 0">—</span>
            <span class="muted" *ngIf="c.pending.length">{{ c.pending.join(', ') }}</span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Serverless + Jobs: the half that is not EC2 -->
  <ng-container *ngIf="status">
    <h5 class="section-title">Lambdas, Trigger, ECS &amp; Jobs</h5>
    <p class="section-hint">
      Eine Umgebung ist erst dann wirklich gestoppt, wenn auch diese Seite steht.
      Der Job-Schalter erreicht jeden Pod mit bis zu ~15&nbsp;s Verzögerung.
    </p>

    <div class="serverless-summary">
      <span class="label" [ngClass]="jobsPaused ? 'label-warning' : 'label-success'">
        Backend-Jobs: {{ jobsPaused ? 'pausiert' : 'laufen' }}
      </span>
      <span class="label label-light" *ngFor="let s of serverlessSummary">
        {{ s.label }}: {{ s.done }}/{{ s.total }}
      </span>
      <span class="label label-light" *ngIf="!status.serverless">
        Serverless-Status nicht abrufbar
      </span>
    </div>

    <table class="table table-compact instance-table" *ngIf="runningTasks.length">
      <thead>
        <tr>
          <th class="left">Laufender ECS-Task</th>
          <th class="left">Cluster</th>
          <th class="left">Status</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let t of runningTasks">
          <td class="left">{{ t.family }}</td>
          <td class="left">{{ t.cluster }}</td>
          <td class="left"><span class="label label-warning">{{ t.lastStatus }}</span></td>
        </tr>
      </tbody>
    </table>
  </ng-container>

  <!-- Auto Scaling -->
  <ng-container *ngIf="status">
    <h5 class="section-title" *ngIf="status.autoScalingGroups.length">Auto-Scaling-Gruppen</h5>
    <table class="table table-compact asg-table" *ngIf="status.autoScalingGroups.length">
      <thead>
        <tr>
          <th class="left">Gruppe</th>
          <th>Min</th>
          <th>Soll</th>
          <th>Max</th>
          <th>Instanzen</th>
          <th class="left">Angehalten</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let g of status.autoScalingGroups">
          <td class="left">{{ g.name }}</td>
          <td>{{ g.minSize }}</td>
          <td>{{ g.desiredCapacity }}</td>
          <td>{{ g.maxSize }}</td>
          <td>{{ g.instanceCount }}</td>
          <td class="left">
            <span class="label label-warning" *ngIf="g.suspendedProcesses.length">
              {{ g.suspendedProcesses.length }} Prozesse
            </span>
            <span class="muted" *ngIf="!g.suspendedProcesses.length">—</span>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Instanzen -->
    <div class="instance-group" *ngFor="let group of groups">
      <h5 class="section-title">
        {{ group.title }}
        <span class="count">({{ group.instances.length }})</span>
      </h5>
      <p class="section-hint">{{ group.hint }}</p>
      <table class="table table-compact instance-table">
        <thead>
          <tr>
            <th class="left">Name</th>
            <th class="left">Typ</th>
            <th class="left">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let i of group.instances">
            <td class="left instance-name" [title]="i.instanceId">{{ i.name || i.instanceId }}</td>
            <td class="left">{{ i.instanceType }}</td>
            <td class="left">
              <span class="label" [ngClass]="stateClass(i.state)">{{ stateLabel(i.state) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="muted" *ngIf="!status.instances.length">
      Keine Instanzen dieser Umgebung gefunden.
    </p>
  </ng-container>

  <!-- Deaktivieren: getippte Bestätigung -->
  <clr-modal [(clrModalOpen)]="deactivateDialogOpen" [clrModalClosable]="running !== 'deactivate'">
    <h3 class="modal-title">Umgebung {{ stage }} deaktivieren</h3>
    <div class="modal-body">
      <p>
        Terminiert die wieder aufbaubaren GPU-Instanzen dieser Umgebung
        (Voxtral Realtime inkl. Standby, Whisper transcribe-live) und fährt die
        Auto-Scaling-Gruppen auf 0.
      </p>
      <p class="deactivate-warning">
        <cds-icon shape="warning-standard" solid></cds-icon>
        Terminierte Instanzen sind weg — laufende Sitzungen enden sofort.
        Die dauerhaften Dienste werden nur gestoppt und lassen sich mit „Aktivieren" zurückholen.
      </p>
      <p class="live-warning" *ngIf="liveCount > 0">
        <cds-icon shape="exclamation-triangle" solid></cds-icon>
        Achtung: {{ liveCount }} Live-Transkription(en) laufen gerade.
      </p>

      <label class="confirm-label" for="deactivate-confirm">
        Zur Bestätigung <strong>{{ stage }}</strong> eintippen:
      </label>
      <input id="deactivate-confirm" type="text" class="clr-input confirm-input"
             [(ngModel)]="confirmText" [disabled]="running === 'deactivate'"
             autocomplete="off" spellcheck="false" />
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-outline"
              (click)="deactivateDialogOpen = false" [disabled]="running === 'deactivate'">
        Abbrechen
      </button>
      <button type="button" class="btn btn-danger"
              (click)="confirmDeactivate()"
              [disabled]="!confirmMatches || running === 'deactivate'">
        <span *ngIf="running !== 'deactivate'">Deaktivieren</span>
        <span *ngIf="running === 'deactivate'">Wird deaktiviert…</span>
      </button>
    </div>
  </clr-modal>

</div>
